import type {Activity} from 'APIs/letrusAPI/activities/types';

export interface FocusModeCardState {
  buttonText: string;
  enabled: boolean;
  stepHighlightPredicate?: (order: number) => boolean;
}

export function fallbackDisplayFromActivityStatus(
  activity: Activity,
): FocusModeCardState {
  const defaultTextMap: Record<string, string> = {
    in_progress: activity?.started
      ? 'Continuar atividade'
      : 'Iniciar atividade',
    opened: 'Iniciar atividade',
    scheduled: 'Em breve',
    finished: 'Ver atividade',
    reviewed: 'Ver atividade',
    expired: 'Expirada',
    awaiting: 'Em breve',
  };

  const text = defaultTextMap[activity.status] ?? 'Ver atividade';
  const enabled = !(
    activity.status === 'scheduled' || activity.status === 'expired'
  );

  return {buttonText: text, enabled};
}

export default function getFocusModeCardState(
  activity: Activity,
): FocusModeCardState {
  if (
    activity.status === 'expired' &&
    ['in_progress', 'not_started'].includes(activity.focus_mode_status ?? '')
  ) {
    return fallbackDisplayFromActivityStatus(activity);
  }

  if (!activity?.uses_focus_mode || activity.focus_mode_status == null) {
    return fallbackDisplayFromActivityStatus(activity);
  }

  switch (activity.focus_mode_status) {
    case 'not_started':
      return {buttonText: 'Iniciar atividade', enabled: true};
    case 'in_progress':
      return {buttonText: 'Continuar atividade', enabled: true};
    case 'finished':
      return {buttonText: 'Ver atividade', enabled: true};
    case 'expired_no_answers':
      return {
        buttonText: 'Expirada',
        enabled: false,
      };
    case 'expired_good_progress':
      return {
        buttonText: 'Ver atividade',
        enabled: true,
        stepHighlightPredicate: (order) => order <= (activity.step_number ?? 0),
      };
    case 'expired_focus_mode':
      return {
        buttonText: 'Ver atividade',
        enabled: true,
        // allow access only to first step when expired_focus_mode
        stepHighlightPredicate: (order) => order === 1,
      };
    case 'expired_composition':
      return {
        buttonText: 'Ver atividade',
        enabled: true,
        stepHighlightPredicate: (order) => order <= (activity.step_number ?? 0),
      };
    case 'expired_activity':
      return {
        buttonText: 'Ver atividade',
        enabled: Boolean(activity.step_number && activity.step_number > 0),
        stepHighlightPredicate: (order) => order <= (activity.step_number ?? 0),
      };
    default:
      return fallbackDisplayFromActivityStatus(activity);
  }
}
